import { z } from 'zod';
import { UserRole } from '../user';
import { IdentityProvider, LinkRequiredReasonEnum } from './auth.enums';

const PASSWORD_REGEX = new RegExp(
  '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[ !"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~]).{8,}$',
);

const PASSWORD_MESSAGE =
  'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character.';

export const PasswordSchema = z
  .string()
  .min(8, PASSWORD_MESSAGE)
  .regex(PASSWORD_REGEX, PASSWORD_MESSAGE);

/**
 * Zod schema for registration request.
 */
export const RegisterRequestSchema = z.object({
  email: z.email().toLowerCase(),
  password: PasswordSchema,
  firstName: z.string(),
  lastName: z.string(),
  role: z.enum(UserRole).optional(),
});

/**
 * Zod schema for registration response.
 */
export const RegisterResponseSchema = z.object({
  username: z.string(),
  reference: z.string(),
  role: z.enum(UserRole).optional(),
});

/**
 * Zod schema for login request.
 */
export const LoginRequestSchema = z.object({
  email: z.email().toLowerCase(),
  password: PasswordSchema,
  role: z.enum(UserRole).optional(),
});

export const VerifyOtpRequestSchema = z.object({
  otp: z.string().length(4),
  reference: z.string(),
  type: z.enum(['registration', 'passwordReset']),
});

export const ResendOtpRequestSchema = z.object({
  reference: z.string(),
  type: z.enum(['registration', 'passwordReset']),
});

export const VerifyOtpResponseSchema = z.object({
  isValid: z.boolean(),
  message: z.string(),
  reference: z.string().optional(),
});

/**
 * Zod schema for login response.
 */
export const LoginResponseSchema = z.object({
  AccessToken: z.string(),
  RefreshToken: z.string(),
  IdToken: z.string(),
  ExpiresIn: z.number(),
  TokenType: z.literal('Bearer'),
  /** The user's roles bitmask decomposed into an array of UserRole values. */
  availableRoles: z.array(z.enum(UserRole)),
  /** The role the user was last active as, or null on first login / never-switched. */
  lastActiveRole: z.enum(UserRole).nullable(),
});

/**
 * Zod schema for switch profile request.
 */
export const SwitchProfileRequestSchema = z.object({
  role: z.enum(UserRole),
});

/**
 * Zod schema for switch profile response.
 */
export const SwitchProfileResponseSchema = z.object({
  lastActiveRole: z.enum(UserRole),
});

/**
 * Zod schema for refresh token request.
 */
export const RefreshTokenRequestSchema = z.object({
  refreshToken: z.string(),
});

export const LogoutRequestSchema = z.object({
  refreshToken: z.string(),
  accessToken: z.string(),
});

/**
 * Zod schema for forgot password request.
 */
export const ForgotPasswordRequestSchema = z.object({
  email: z.email().toLowerCase(),
});

export const ForgotPasswordResponseSchema = z.object({
  destination: z.string(),
  deliveryMedium: z.string(),
  attributeName: z.string(),
});

/**
 * Zod schema for reset password request.
 */
export const ResetPasswordRequestSchema = z.object({
  token: z.string(),
  newPassword: PasswordSchema,
});

/**
 * Zod schema for change password request.
 */
export const ChangePasswordRequestSchema = z.object({
  currentPassword: PasswordSchema,
  newPassword: PasswordSchema,
});

export const OauthStartQuerySchema = z.object({
  redirectTo: z.string().min(1).max(2048).optional(),
});

export const OauthStartResponseSchema = z.object({
  authorizeUrl: z.url(),
});

export const OauthExchangeRequestSchema = z.object({
  handoff: z.string().min(16).max(128),
});

export const OauthDirectTokenRequestSchema = z.object({
  idToken: z.string().min(1).max(8192),
  nonce: z.string().min(8).max(256),
  authorizationCode: z.string().min(1).max(1024).optional(),
  user: z
    .object({
      name: z
        .object({
          firstName: z.string().max(128).nullable().optional(),
          lastName: z.string().max(128).nullable().optional(),
        })
        .optional(),
    })
    .optional(),
});

export const OauthLinkCompleteRequestSchema = z
  .object({
    linkHandoff: z.string().min(16).max(128),
    password: PasswordSchema.optional(),
    otpReference: z.string().min(1).max(128).optional(),
    otp: z.string().length(4).optional(),
  })
  .refine(
    (v) => (v.password ? true : v.otpReference && v.otp),
    'either `password` OR (`otpReference` AND `otp`) must be provided',
  );

export const OauthLinkSendOtpRequestSchema = z.object({
  linkHandoff: z.string().min(16).max(128),
});

export const LinkRequiredResponseSchema = z.object({
  kind: z.literal('link_required'),
  reason: z.enum(LinkRequiredReasonEnum),
  linkHandoff: z.string(),
  maskedEmail: z.string(),
  hasPassword: z.boolean(),
  provider: z.enum(IdentityProvider),
});

export const OauthLinkSendOtpResponseSchema = z.object({
  otpReference: z.string(),
});

export const ProvidersListItemSchema = z.object({
  name: z.enum(IdentityProvider),
  enabled: z.boolean(),
  canSignUp: z.boolean(),
});

export const ProvidersListResponseSchema = z.object({
  providers: z.array(ProvidersListItemSchema),
});

export const OauthLinkCompleteEmailRequestSchema = z.object({
  linkHandoff: z.string().min(16).max(128),
  email: z.email().max(320),
});
